import type { AdvisorInvestor } from '@/store/usersApi';
import { OnboardingStepNumber } from '@/utils/enums';

/** Minimal client shape needed to evaluate Invite/Delete action eligibility. */
export type EligibilityInput = Pick<
  AdvisorInvestor,
  'OnboardingStep' | 'CanInvite' | 'CanDelete' | 'UserId'
>;

const hasValidUserId = (client: EligibilityInput): boolean =>
  Number.isFinite(client.UserId);

/**
 * Whether the client is at a pre-registration onboarding step (Not Invited or
 * Invited). Whitelisting steps 1-2 — rather than excluding only step 3 — keeps
 * any other/unexpected status out of Invite/Delete, so a stale flag on a
 * registered-but-non-3 row can never make it eligible.
 */
const isPreRegistrationStep = (client: EligibilityInput): boolean =>
  client.OnboardingStep === OnboardingStepNumber.NOT_INVITED ||
  client.OnboardingStep === OnboardingStepNumber.INVITED;

/**
 * Invite is eligible for Not Invited / Invited clients (onboarding steps 1-2)
 * that the API flags as invitable (`CanInvite`). Active/registered clients — and
 * any other status — are never eligible, even if a stale `CanInvite` flag says
 * otherwise. Shared by the bulk (IU-270) and row-level (IU-271) client actions.
 */
export function isInviteEligible(client: EligibilityInput): boolean {
  return (
    isPreRegistrationStep(client) && client.CanInvite && hasValidUserId(client)
  );
}

/**
 * Delete mirrors invite: Not Invited / Invited clients with the API `CanDelete`
 * flag set. Active/registered clients and any other status are excluded
 * regardless of the flag.
 */
export function isDeleteEligible(client: EligibilityInput): boolean {
  return (
    isPreRegistrationStep(client) && client.CanDelete && hasValidUserId(client)
  );
}

/**
 * Invitation History is visible for any Not Invited / Invited client, keyed off
 * onboarding status alone — NOT the `CanInvite` flag. A Not Invited client that
 * the API can't currently invite (`CanInvite === false`) must still surface the
 * grid so its "This user has not been invited yet." empty state can render
 * (IU-249). Active/registered clients are excluded.
 */
export function isInvitationHistoryEligible(client: EligibilityInput): boolean {
  return isPreRegistrationStep(client) && hasValidUserId(client);
}
